/**
 * Iframe-side helpers for the embed surface.
 *
 * Outbound: emit lifecycle events to the parent window.
 * Inbound: receive a config message from the parent, with strict
 * origin, source, and schema validation.
 *
 * @module lib/embed/postMessage
 */

/** Lifecycle events the iframe emits to the parent. */
export type EmbedLifecycleEvent =
  | 'ready'
  | 'loaded'
  | 'response_started'
  | 'response_complete'
  | 'close'
  | 'error';

/** Config sent by the parent that controls embed UX. */
export interface EmbedConfig {
  collectInfo: 'pre' | 'post' | 'never';
}

const VALID_COLLECT_INFO: ReadonlyArray<EmbedConfig['collectInfo']> = [
  'pre',
  'post',
  'never',
];

const CONFIG_MESSAGE_TYPE = 'hollie:config';

/**
 * Emit a lifecycle event to the parent window. No-op if the iframe is
 * not actually embedded (parent equals self). Lifecycle payloads are
 * non-sensitive so we use targetOrigin "*".
 */
export function emitToParent(
  event: EmbedLifecycleEvent,
  payload?: unknown,
): void {
  if (typeof window === 'undefined' || window.parent === window) {
    return;
  }
  const message = payload === undefined
    ? { type: `hollie:${event}` }
    : { type: `hollie:${event}`, payload };
  window.parent.postMessage(message, '*');
}

/**
 * Subscribe to config messages from the parent window.
 * Validates `event.origin` against the supplied expected origin,
 * `event.source` against `window.parent` (rejects sibling/nested
 * iframes), and the payload schema. Returns an unsubscribe function.
 */
export function subscribeConfig(
  callback: (config: EmbedConfig) => void,
  expectedParentOrigin: string,
): () => void {
  const handler = (event: MessageEvent): void => {
    if (event.origin !== expectedParentOrigin) return;
    if (event.source !== window.parent) return;

    const data = event.data;
    if (typeof data !== 'object' || data === null) return;
    if ((data as { type?: unknown }).type !== CONFIG_MESSAGE_TYPE) return;

    const payload = (data as { payload?: unknown }).payload;
    if (typeof payload !== 'object' || payload === null) return;

    const collectInfo = (payload as { collectInfo?: unknown }).collectInfo;
    if (!VALID_COLLECT_INFO.includes(collectInfo as EmbedConfig['collectInfo'])) return;

    callback({ collectInfo: collectInfo as EmbedConfig['collectInfo'] });
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

/**
 * Parse the parent origin from the iframe URL's `embed_origin` query param.
 * Accepts only https origins, plus http://localhost and http://127.0.0.1
 * for development. Returns null for anything else.
 */
export function parseExpectedParentOrigin(input: string | null | undefined): string | null {
  if (!input) return null;
  let parsed: URL;
  try {
    parsed = new URL(input);
  } catch {
    return null;
  }

  if (parsed.protocol === 'https:') {
    return parsed.origin;
  }

  if (
    parsed.protocol === 'http:' &&
    (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')
  ) {
    return parsed.origin;
  }

  return null;
}
